import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { logout } from "../utilities/logout";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Sales dashboard / filter API calls interpret the selected calendar dates as full days in this zone
 * (midnight→end-of-day US Eastern, DST-aware). Matches typical US ops reporting regardless of browser TZ.
 */
export const SALES_DASHBOARD_FILTER_TZ = "America/New_York";

const dataAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/`,
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

dataAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

dataAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return dataAPI(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refresh = localStorage.getItem("refreshToken");
        if (!refresh) throw new Error("No refresh token");
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/api/token/refresh/`,
          { refresh }
        );
        localStorage.setItem("accessToken", data.access);
        processQueue(null, data.access);
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
        return dataAPI(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper: selected range → inclusive UTC ISO instants for [start of day, end of day] in US Eastern.
const formatDateRange = (dateRange) => {
  if (!dateRange || dateRange.length !== 2) return dateRange;
  const [a, b] = dateRange;
  if (a == null || b == null) return dateRange;

  const calendarYmd = (x) => {
    if (typeof x === "string") {
      return dayjs(x).tz(SALES_DASHBOARD_FILTER_TZ).format("YYYY-MM-DD");
    }
    if (x && typeof x.format === "function") {
      return x.format("YYYY-MM-DD");
    }
    return dayjs(x).tz(SALES_DASHBOARD_FILTER_TZ).format("YYYY-MM-DD");
  };

  const startYmd = calendarYmd(a);
  const endYmd = calendarYmd(b);
  if (!startYmd || !endYmd) return dateRange;

  const start = dayjs.tz(startYmd, SALES_DASHBOARD_FILTER_TZ).startOf("day");
  const end = dayjs.tz(endYmd, SALES_DASHBOARD_FILTER_TZ).endOf("day");

  return [
    start.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
    end.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
  ];
};

/** Coerce `filters.brands` to integers (Redux may store string ids). Mutates `formattedFilters`. */
const coerceFilterBrandIds = (formattedFilters) => {
  if (!formattedFilters || !Array.isArray(formattedFilters.brands)) return;
  formattedFilters.brands = formattedFilters.brands
    .map((b) => (typeof b === "string" ? parseInt(b, 10) : Number(b)))
    .filter((n) => !Number.isNaN(n));
};

/** Normalize list endpoints that may return a raw array or DRF-style { results } / { data }. */
const extractResponseList = (res) => {
  if (res == null) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.results)) return res.results;
  if (Array.isArray(res.data)) return res.data;
  return [];
};

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    dataAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

export const fetchSublyticsOrders = async (
  brandId,
  page = 1,
  pageSize = 100,
  filters = null,
  requestConfig = {}
) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = { ...filters };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    coerceFilterBrandIds(formattedFilters);

    // Add pagination parameters - use page_size to match backend
    formattedFilters.page = page;
    formattedFilters.page_size = pageSize;

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    const response = await dataAPI.post(
      `fetch-data/live/sublytics/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    // Use the original GET request if no filters
    const response = await dataAPI.get(
      `fetch-data/sublytics-orders/?brand=${brandId}&page=${page}&page_size=${pageSize}`,
      requestConfig
    );
    return response.data;
  }
};

export const syncSublyticsOrders = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand_id: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  const response = await dataAPI.post(
    `fetch-data/sublytics-orders/sync_sublytics/`,
    body,
    requestConfig
  );
  return response.data;
};

// WordPress Orders API functions
export const fetchWordPressOrders = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = { ...filters };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    // Coerce brand ids so Django sees integers (Redux may store strings)
    if (Array.isArray(formattedFilters.brands)) {
      formattedFilters.brands = formattedFilters.brands
        .map((b) => (typeof b === "string" ? parseInt(b, 10) : Number(b)))
        .filter((n) => !Number.isNaN(n));
    }

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    const response = await dataAPI.post(
      `fetch-data/live/wordpress/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    // Use the original GET request if no filters
    const response = await dataAPI.get(
      `fetch-data/web-orders/?brand=${brandId}`,
      requestConfig
    );
    return response.data;
  }
};

export const syncWordPressOrders = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  const response = await dataAPI.post(
    `fetch-data/web-orders/sync_wordpress/`,
    body,
    requestConfig
  );
  return response.data;
};

export const fetchBatches = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/batches/?gateway__brand=${brandId}`);
  return response.data;
};

export const fetchTransactionsByBatch = async (batchId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/transactions/?batch=${batchId}`);
  return response.data;
};

export const fetchSalesReport = async (filters) => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  const response = await dataAPI.post("fetch-data/sales-report/", { filters: formattedFilters });
  return response.data;
};

export const fetchTransactions = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = { ...filters };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    coerceFilterBrandIds(formattedFilters);

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    const response = await dataAPI.post(
      `fetch-data/live/authnet/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    // Use the original GET request if no filters
    const response = await dataAPI.get(
      `fetch-data/transactions/?gateway__brand=${brandId}`,
      requestConfig
    );
    return response.data;
  }
};

export const syncTransactions = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand_id: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  const response = await dataAPI.post(
    `fetch-data/transactions/fetch_all/`,
    body,
    requestConfig
  );
  return response.data;
};

export const fetchStripeTransactions = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = { ...filters };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    coerceFilterBrandIds(formattedFilters);

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    const response = await dataAPI.post(`fetch-data/live/stripe/`, {
      filters: formattedFilters,
    }, requestConfig);
    return response.data;
  } else {
    // Use the original GET request if no filters
    const response = await dataAPI.get(`fetch-data/stripe-transactions/?gateway__brand=${brandId}`, requestConfig);
    return response.data;
  }
};

export const syncStripeTransactions = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  const response = await dataAPI.post(
    `fetch-data/stripe-transactions/fetch_all/`,
    body,
    requestConfig
  );
  return response.data;
};

export const fetchStripeTransactionDetails = async (transactionId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/stripe-transactions/${transactionId}/`);
  return response.data;
};

export const fetchNMITransactions = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = { ...filters };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    coerceFilterBrandIds(formattedFilters);

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    const response = await dataAPI.post(
      `fetch-data/live/nmi/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    // Use the original GET request if no filters
    const response = await dataAPI.get(
      `fetch-data/nmi-transactions/?gateway_credentials__brand=${brandId}`,
      requestConfig
    );
    return response.data;
  }
};

export const syncNMITransactions = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  try {
    const response = await dataAPI.post(
      `fetch-data/nmi-transactions/fetch_all/`,
      body,
      requestConfig
    );
    return response.data;
  } catch (err) {
    const data = err.response?.data;
    const msg = typeof data?.error === "string" ? data.error : "";
    // Legacy API: “no gateways for brand” was 404; treat like TigerTech no_credentials for sync UX.
    if (err.response?.status === 404 && msg.includes("No NMI gateways")) {
      return { no_credentials: true, success: false, message: msg };
    }
    throw err;
  }
};

// RPM (call center) live orders — single shared connection, filter by date range.
// Mirrors the other live sources: POST filters, backend groups OLX_Detail lines
// into orders and returns { results: [...] }.
export const fetchRPMOrders = async (filters = null, requestConfig = {}) => {
  setAuthHeader();

  const formattedFilters = { ...(filters || {}) };
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }
  coerceFilterBrandIds(formattedFilters);

  const response = await dataAPI.post(
    `fetch-data/live/rpm/`,
    { filters: formattedFilters },
    requestConfig
  );
  return response.data;
};

// TigerTech Sales API functions
export const fetchTigerTechSales = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();

  // If filters are provided, use POST with filters
  if (filters) {
    // Create a copy of filters to avoid mutating the original
    const formattedFilters = {
      page: 1,
      page_size: 5000,
      ...filters,
    };

    // Format date range if it exists
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }

    coerceFilterBrandIds(formattedFilters);

    // If brandId is provided but not in filters, add it
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }

    if (formattedFilters.source == null) {
      formattedFilters.source = "TigerTech";
    }

    const response = await dataAPI.post(
      `fetch-data/tigertech-sales/filter/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    // Use the original GET request if no filters
    const sourceParam = filters && filters.source ? `&source=${filters.source}` : "";
    const response = await dataAPI.get(
      `fetch-data/tigertech-sales/?brand=${brandId}${sourceParam}`,
      requestConfig
    );
    return response.data;
  }
};

/**
 * Dashboard: many small /filter/ pages instead of one huge request (avoids 240s+ timeouts).
 */
export const fetchTigerTechSalesBatched = async (
  brandId,
  baseFilters,
  requestConfig = {},
  {
    pageSize = 400,
    maxRows = 4000,
    timeoutPerPage = 75000,
    shouldStop = () => false,
  } = {}
) => {
  const acc = [];
  let page = 1;
  while (acc.length < maxRows) {
    if (shouldStop()) return acc;
    const res = await fetchTigerTechSales(
      brandId,
      { ...baseFilters, page, page_size: pageSize },
      { ...requestConfig, timeout: timeoutPerPage }
    );
    const chunk = extractResponseList(res);
    if (!chunk.length) break;
    acc.push(...chunk);
    if (chunk.length < pageSize) break;
    page += 1;
    if (page > 30) break;
  }
  return acc;
};

export const syncTigerTechSales = async (
  brandId,
  source = "TigerTech",
  requestConfig = {},
  dateRange = null
) => {
  setAuthHeader();
  const body = { brand_id: brandId, source };
  if (dateRange) body.date_range = dateRange;
  const response = await dataAPI.post(
    `fetch-data/tigertech-sales/sync/`,
    body,
    requestConfig
  );
  return response.data;
};

export const fetchTigerTechSFTPCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/tigertech-sftp-credentials/?brand=${brandId}`);
  return response.data;
};

export const createTigerTechSFTPCredential = async (credentialData) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/tigertech-sftp-credentials/`, credentialData);
  return response.data;
};

export const updateTigerTechSFTPCredential = async (id, credentialData) => {
  setAuthHeader();
  const response = await dataAPI.put(
    `fetch-data/tigertech-sftp-credentials/${id}/`,
    credentialData
  );
  return response.data;
};

export const fetchTigerTechSaleById = async (tigerTechSaleId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/tigertech-sales/${tigerTechSaleId}/`);
  return response.data;
};

export const fetchOrdersWithoutMatches = async (filters, matchType = "missing_tigertech") => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  // Format date range if it exists
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  // Add match type to filters
  formattedFilters.match_type = matchType;

  const response = await dataAPI.post(`fetch-data/sublytics-orders/orders_without_matches/`, {
    filters: formattedFilters,
  });
  return response.data;
};

export const fetchTigerTechSalesWithoutMatches = async (filters) => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  // Format date range if it exists
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  const response = await dataAPI.post(`fetch-data/tigertech-sales/sales-without-matches/`, {
    filters: formattedFilters,
  });
  return response.data;
};

export const fetchSalesUpdateSummary = async (filters) => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  // Format date range if it exists
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  // Prepare query params for TigerTechSales endpoints
  const startDate = formattedFilters.dateRange ? formattedFilters.dateRange[0].split("T")[0] : "";
  const endDate = formattedFilters.dateRange ? formattedFilters.dateRange[1].split("T")[0] : "";
  const brandId =
    formattedFilters.brands && formattedFilters.brands.length > 0 ? formattedFilters.brands[0] : "";

  // Fetch data from all endpoints - no longer need separate transaction API calls
  // as we're using the real transaction data directly from the sales-update-summary endpoints
  const [sublyticsResponse, webOrdersResponse, tigerTechResponse, nexRepResponse] =
    await Promise.all([
      dataAPI.post(`fetch-data/sublytics-orders/sales-update-summary/`, {
        filters: formattedFilters,
      }),
      dataAPI.post(`fetch-data/web-orders/sales-update-summary/`, { filters: formattedFilters }),
      dataAPI.get(
        `fetch-data/tigertech-sales/sales-update-summary/?brand=${brandId}&source=TigerTech&start_date=${startDate}&end_date=${endDate}`
      ),
      dataAPI.get(
        `fetch-data/tigertech-sales/sales-update-summary/?brand=${brandId}&source=Socrate&start_date=${startDate}&end_date=${endDate}`
      ),
    ]);

  return {
    sublyticsData: sublyticsResponse?.data,
    webOrdersData: webOrdersResponse.data,
    tigerTechData: tigerTechResponse.data,
    nexRepData: nexRepResponse.data,
  };
};

export const fetchOrdersWithoutRealTransactions = async (
  filters,
  transactionType,
  merchantName
) => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  // Format date range if it exists
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  // Call the new endpoint with all required parameters
  const response = await dataAPI.post(
    `fetch-data/sublytics-orders/orders_without_real_transactions/`,
    {
      filters: formattedFilters,
      transaction_type: transactionType, // 'auth', 'stripe', or 'nmi'
      merchant_name: merchantName,
    }
  );

  return response.data;
};

export const fetchWebOrdersWithoutStripe = async (filters, merchantName) => {
  setAuthHeader();

  // Create a copy of filters to avoid mutating the original
  const formattedFilters = { ...filters };

  // Format date range if it exists
  if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
    formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
  }

  // Call the endpoint to get web orders without Stripe transactions
  const response = await dataAPI.post(`fetch-data/web-orders/without_stripe_transactions/`, {
    filters: formattedFilters,
    merchant_name: merchantName,
  });

  return response.data;
};


// ============================================================
// Platform Credential Management APIs
// ============================================================

// --- Auth.NET (Gateway Credentials) ---
export const fetchGatewayCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/gateway-credentials/?brand=${brandId}`);
  return response.data;
};

export const createGatewayCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/gateway-credentials/`, data);
  return response.data;
};

export const updateGatewayCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/gateway-credentials/${id}/`, data);
  return response.data;
};

export const deleteGatewayCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/gateway-credentials/${id}/`);
  return response.data;
};

// --- WordPress Credentials ---
export const fetchWordPressCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/wordpress-credentials/?brand=${brandId}`);
  return response.data;
};

export const createWordPressCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/wordpress-credentials/`, data);
  return response.data;
};

export const updateWordPressCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/wordpress-credentials/${id}/`, data);
  return response.data;
};

export const deleteWordPressCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/wordpress-credentials/${id}/`);
  return response.data;
};

// --- Stripe Gateway Credentials ---
export const fetchStripeGatewayCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/stripe-gateway-credentials/?brand=${brandId}`);
  return response.data;
};

export const createStripeGatewayCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/stripe-gateway-credentials/`, data);
  return response.data;
};

export const updateStripeGatewayCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/stripe-gateway-credentials/${id}/`, data);
  return response.data;
};

export const deleteStripeGatewayCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/stripe-gateway-credentials/${id}/`);
  return response.data;
};

// --- Sublytics Credentials ---
export const fetchSublyticsCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/sublytics-credentials/?brand=${brandId}`);
  return response.data;
};

export const createSublyticsCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/sublytics-credentials/`, data);
  return response.data;
};

export const updateSublyticsCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/sublytics-credentials/${id}/`, data);
  return response.data;
};

export const deleteSublyticsCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/sublytics-credentials/${id}/`);
  return response.data;
};

// --- NMI Payarc Gateway Credentials ---
export const fetchNMIGatewayCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/nmi-gateway-credentials/?brand=${brandId}`);
  return response.data;
};

export const createNMIGatewayCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/nmi-gateway-credentials/`, data);
  return response.data;
};

export const updateNMIGatewayCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/nmi-gateway-credentials/${id}/`, data);
  return response.data;
};

export const deleteNMIGatewayCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/nmi-gateway-credentials/${id}/`);
  return response.data;
};

// --- ShipStation Credentials ---
export const fetchShipStationCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/shipstation-credentials/?brand=${brandId}`);
  return response.data;
};

export const createShipStationCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/shipstation-credentials/`, data);
  return response.data;
};

export const updateShipStationCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/shipstation-credentials/${id}/`, data);
  return response.data;
};

export const deleteShipStationCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/shipstation-credentials/${id}/`);
  return response.data;
};

// --- Delete TigerTech SFTP Credential ---
export const deleteTigerTechSFTPCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/tigertech-sftp-credentials/${id}/`);
  return response.data;
};

// --- Reliable Gateway Transactions (Check Gateway) ---
export const fetchReliableTransactions = async (brandId, filters = null, requestConfig = {}) => {
  setAuthHeader();
  if (filters) {
    const formattedFilters = { ...filters };
    if (formattedFilters.dateRange && formattedFilters.dateRange.length === 2) {
      formattedFilters.dateRange = formatDateRange(formattedFilters.dateRange);
    }
    coerceFilterBrandIds(formattedFilters);
    if (brandId != null && brandId !== "" && !formattedFilters.brands?.length) {
      const n = typeof brandId === "string" ? parseInt(brandId, 10) : Number(brandId);
      if (!Number.isNaN(n)) formattedFilters.brands = [n];
    }
    const response = await dataAPI.post(
      `fetch-data/reliable-transactions/filter/`,
      { filters: formattedFilters },
      requestConfig
    );
    return response.data;
  } else {
    const response = await dataAPI.get(
      `fetch-data/reliable-transactions/?credential__brand=${brandId}`,
      requestConfig
    );
    return response.data;
  }
};

export const syncReliableTransactions = async (brandId, requestConfig = {}, dateRange = null) => {
  setAuthHeader();
  const body = { brand_id: brandId };
  if (dateRange?.length === 2) {
    const formatted = formatDateRange(dateRange);
    body.date_from = formatted[0];
    body.date_to = formatted[1];
  }
  try {
    const response = await dataAPI.post(
      `fetch-data/reliable-transactions/fetch_all/`,
      body,
      requestConfig
    );
    return response.data;
  } catch (err) {
    const data = err.response?.data;
    const msg = typeof data?.error === "string" ? data.error : "";
    if (err.response?.status === 404 && msg.includes("No Reliable")) {
      return { no_credentials: true, success: false, message: msg };
    }
    throw err;
  }
};

// --- Reliable Gateway (Check Gateway) Credentials ---
export const fetchReliableGatewayCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/reliable-gateway-credentials/?brand=${brandId}`);
  return response.data;
};

export const createReliableGatewayCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/reliable-gateway-credentials/`, data);
  return response.data;
};

export const updateReliableGatewayCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/reliable-gateway-credentials/${id}/`, data);
  return response.data;
};

export const deleteReliableGatewayCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/reliable-gateway-credentials/${id}/`);
  return response.data;
};

// --- PAS RADE Credentials ---
export const fetchPasRadeCredentials = async (brandId) => {
  setAuthHeader();
  const response = await dataAPI.get(`fetch-data/pasrade-credentials/?brand=${brandId}`);
  return response.data;
};

export const createPasRadeCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/pasrade-credentials/`, data);
  return response.data;
};

export const updatePasRadeCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/pasrade-credentials/${id}/`, data);
  return response.data;
};

export const deletePasRadeCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/pasrade-credentials/${id}/`);
  return response.data;
};

// --- Elavon Credentials ---
export const fetchElavonCredentials = async (brandId) => {
  setAuthHeader();
  const q =
    brandId != null && brandId !== "" && String(brandId) !== "undefined"
      ? `?brand=${encodeURIComponent(brandId)}`
      : "";
  const response = await dataAPI.get(`fetch-data/elavon-credentials/${q}`);
  return response.data;
};

export const createElavonCredential = async (data) => {
  setAuthHeader();
  const response = await dataAPI.post(`fetch-data/elavon-credentials/`, data);
  return response.data;
};

export const updateElavonCredential = async (id, data) => {
  setAuthHeader();
  const response = await dataAPI.patch(`fetch-data/elavon-credentials/${id}/`, data);
  return response.data;
};

export const deleteElavonCredential = async (id) => {
  setAuthHeader();
  const response = await dataAPI.delete(`fetch-data/elavon-credentials/${id}/`);
  return response.data;
};

// --- Elavon Transactions ---
export const fetchElavonTransactions = async (filters = {}, requestConfig = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.brand_ids) {
    (Array.isArray(filters.brand_ids) ? filters.brand_ids : [filters.brand_ids]).forEach(
      (id) => params.append("brand_ids[]", id)
    );
  }
  if (filters.dateRange) {
    params.append("date_from", filters.dateRange[0]);
    params.append("date_to", filters.dateRange[1]);
  }
  const response = await dataAPI.get(
    `Report/api/elavon-transactions/?${params.toString()}`,
    requestConfig
  );
  return response.data;
};

// --- Elavon Interactive Sync (OTP flow) ---
export const startElavonSession = async (data = {}) => {
  setAuthHeader();
  const response = await dataAPI.post(`Report/api/elavon-start/`, data);
  return response.data;
};

export const submitElavonOtp = async (sessionId, otp) => {
  setAuthHeader();
  const response = await dataAPI.post(`Report/api/elavon-otp/`, {
    session_id: sessionId,
    otp,
  });
  return response.data;
};

export const pollElavonStatus = async (sessionId) => {
  setAuthHeader();
  const response = await dataAPI.get(
    `Report/api/elavon-status/?session_id=${encodeURIComponent(sessionId)}`
  );
  return response.data;
};

// --- Elavon Downloads ---
export const fetchElavonDownloads = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.brand_ids) {
    (Array.isArray(filters.brand_ids) ? filters.brand_ids : [filters.brand_ids]).forEach(
      (id) => params.append("brand_ids[]", id)
    );
  }
  if (filters.date_from) params.append("date_from", filters.date_from);
  if (filters.date_to) params.append("date_to", filters.date_to);
  const response = await dataAPI.get(
    `Report/api/elavon-downloads/?${params.toString()}`
  );
  return response.data;
};

// --- Dashboard Cron Email Recipients ---
export const getDashboardEmailRecipients = async () => {
  setAuthHeader();
  const response = await dataAPI.get(`Report/api/dashboard-email-recipients/`);
  return response.data;
};

export const saveDashboardEmailRecipients = async (recipients) => {
  setAuthHeader();
  const response = await dataAPI.put(`Report/api/dashboard-email-recipients/`, { recipients });
  return response.data;
};

export const shareDashboardEmail = async (recipients, tabs, dateRange) => {
  setAuthHeader();
  const response = await dataAPI.post(`Report/api/share-dashboard/`, { recipients, tabs, dateRange });
  return response.data;
};

export const uploadMailOrderTracking = async (file) => {
  setAuthHeader();
  const formData = new FormData();
  formData.append("file", file);
  const response = await dataAPI.post(`Report/api/mail-order-tracking/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const fetchMailOrderTracking = async (dateFrom, dateTo) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  const response = await dataAPI.get(`Report/api/mail-order-tracking/?${params.toString()}`);
  return response.data;
};
